import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTasks, useUpdateTask, useDeleteTask } from './useTasksQuery.js';
import TaskCard from './TaskCard.js';
import SortableTaskCard from './SortableTaskCard.js';
import TaskForm from './TaskForm.js';
import { useUIStore, type SortMode } from '../../store/ui.store.js';
import { useLists } from '../lists/useListsQuery.js';
import type { Task } from './tasksApi.js';

interface Props {
  listId?: string;
}

const PRIORITY_WEIGHT: Record<Task['priority'], number> = { high: 0, medium: 1, low: 2 };

function applySort(tasks: Task[], mode: SortMode): Task[] {
  const arr = [...tasks];
  switch (mode) {
    case 'dueDate':
      // Ascending — soonest due date first. Undated tasks sort to the end
      // (Infinity), unlike every other mode here, which sorts descending.
      return arr.sort((a, b) => {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aTime - bTime || (b.sortOrder ?? b.createdAt) - (a.sortOrder ?? a.createdAt);
      });
    case 'manual':
      return arr.sort((a, b) => (b.sortOrder ?? b.createdAt) - (a.sortOrder ?? a.createdAt));
    case 'priority':
      return arr.sort(
        (a, b) =>
          PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority] ||
          (b.sortOrder ?? b.createdAt) - (a.sortOrder ?? a.createdAt)
      );
    case 'created':
      return arr.sort((a, b) => b.createdAt - a.createdAt);
    case 'completed':
      return arr.sort((a, b) => (b.completedAt ?? b.createdAt) - (a.completedAt ?? a.createdAt));
  }
}

function calcSortOrder(tasks: Task[], activeId: string, overId: string): number {
  const oldIndex = tasks.findIndex((t) => t.taskId === activeId);
  const newIndex = tasks.findIndex((t) => t.taskId === overId);
  const reordered = arrayMove(tasks, oldIndex, newIndex);
  const prev = reordered[newIndex - 1];
  const next = reordered[newIndex + 1];
  // Tasks are sorted descending (highest sortOrder first), so prev > moved > next
  if (!prev && !next) return Date.now();
  if (!prev) return (next.sortOrder ?? next.createdAt) + 1_000_000;
  if (!next) return (prev.sortOrder ?? prev.createdAt) - 1_000_000;
  return ((prev.sortOrder ?? prev.createdAt) + (next.sortOrder ?? next.createdAt)) / 2;
}

const SORT_LABELS: Record<SortMode, string> = {
  dueDate: 'Due Date',
  manual: 'Manual',
  priority: 'Priority',
  created: 'Date Created',
  completed: 'Date Completed',
};

export default function TaskList({ listId }: Props) {
  const { data: tasks = [], isLoading, error } = useTasks(listId);
  const { data: lists = [] } = useLists();
  const [showForm, setShowForm] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const sortMode = useUIStore((s) => s.sortMode);
  const setSortMode = useUIStore((s) => s.setSortMode);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (isLoading) return <div className="loading">Loading tasks...</div>;
  if (error) return <div className="error">Failed to load tasks.</div>;

  const topLevel = tasks.filter((t) => !t.parentTaskId);
  const sorted = applySort(topLevel, sortMode);
  const pending = sorted.filter((t) => !t.done);
  const completed = sorted.filter((t) => t.done);
  const isManual = sortMode === 'manual';

  const activeTask = activeId ? topLevel.find((t) => t.taskId === activeId) : null;

  function handleDragStart({ active }: DragStartEvent) {
    setActiveId(active.id as string);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveId(null);
    if (!over || active.id === over.id) return;

    // Determine which section the dragged item belongs to (pending vs completed)
    const inPending = pending.some((t) => t.taskId === active.id);
    const pool = inPending ? pending : completed;
    const newSortOrder = calcSortOrder(pool, active.id as string, over.id as string);

    updateTask.mutate({ taskId: active.id as string, payload: { sortOrder: newSortOrder } });
  }

  function renderCard(task: Task) {
    return (
      <TaskCard
        key={task.taskId}
        task={task}
        onToggle={() => updateTask.mutate({ taskId: task.taskId, payload: { done: !task.done } })}
        onDelete={() => deleteTask.mutate({ taskId: task.taskId, listId: task.listId })}
      />
    );
  }

  function renderSortable(task: Task) {
    return (
      <SortableTaskCard
        key={task.taskId}
        task={task}
        onToggle={() => updateTask.mutate({ taskId: task.taskId, payload: { done: !task.done } })}
        onDelete={() => deleteTask.mutate({ taskId: task.taskId, listId: task.listId })}
      />
    );
  }

  const pendingIds = pending.map((t) => t.taskId);
  const completedIds = completed.map((t) => t.taskId);

  return (
    <div className="task-list">
      <div className="task-list__header">
        <h2 className="task-list__title">
          {listId ? lists.find((l) => l.listId === listId)?.name ?? 'List Tasks' : 'All Tasks'} (
          {pending.length} pending)
        </h2>
        <button className="btn btn--primary" onClick={() => setShowForm(true)}>
          + New Task
        </button>
      </div>

      <div className="task-list__toolbar">
        <span className="task-list__sort-label">Sort by</span>
        {(Object.keys(SORT_LABELS) as SortMode[]).map((mode) => (
          <button
            key={mode}
            className={`btn btn--sm btn--ghost task-list__sort-btn ${sortMode === mode ? 'task-list__sort-btn--active' : ''}`}
            onClick={() => setSortMode(mode)}
          >
            {SORT_LABELS[mode]}
          </button>
        ))}
      </div>

      {showForm && <TaskForm listId={listId} onClose={() => setShowForm(false)} />}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={pendingIds} strategy={verticalListSortingStrategy}>
          <div className="task-list__section">
            {pending.map((task) => (isManual ? renderSortable(task) : renderCard(task)))}
          </div>
        </SortableContext>

        {completed.length > 0 && (
          <details className="task-list__completed">
            <summary>Completed ({completed.length})</summary>
            <SortableContext items={completedIds} strategy={verticalListSortingStrategy}>
              {completed.map((task) => (isManual ? renderSortable(task) : renderCard(task)))}
            </SortableContext>
          </details>
        )}

        <DragOverlay>
          {activeTask && (
            <div style={{ opacity: 0.9, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', borderRadius: 8 }}>
              <TaskCard
                task={activeTask}
                onToggle={() => {}}
                onDelete={() => {}}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {topLevel.length === 0 && !showForm && (
        <div className="task-list__empty">No tasks yet. Add one to get started!</div>
      )}
    </div>
  );
}
